import type { EmailTemplate } from '@/api/types';
import type { SequenceBranchRule } from '@marketing-hub/shared';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { SequenceBranchRulesEditor } from './SequenceBranchRulesEditor';
import styles from './sequenceEditor.module.css';

export type EditableSequenceStep = {
  step_order: number;
  wait_days: number;
  subject_template: string;
  body_text_template: string;
  email_template_id: string | null;
  branch_rules: SequenceBranchRule[];
  step_channel: 'email' | 'voice';
};

type Props = {
  step: EditableSequenceStep;
  steps: EditableSequenceStep[];
  templates: EmailTemplate[];
  onChange: (step: EditableSequenceStep) => void;
  onMove: (delta: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  disabled?: boolean;
};

export function SequenceStepCard({
  step,
  steps,
  templates,
  onChange,
  onMove,
  onDuplicate,
  onDelete,
  canMoveUp,
  canMoveDown,
  disabled,
}: Props) {
  const stepOrders = steps.map((s) => s.step_order);
  const isVoice = step.step_channel === 'voice';
  const tpl = step.email_template_id
    ? templates.find((t) => t.id === step.email_template_id)
    : null;

  return (
    <li className={styles.stepCard}>
      <div className={styles.stepHead}>
        <div>
          <strong>Stap {step.step_order}</strong>
          <span
            className={[styles.stepBadge, isVoice ? styles.stepBadgeVoice : ''].filter(Boolean).join(' ')}
            style={{ marginLeft: '0.5rem' }}
          >
            {isVoice ? 'Voice (stub)' : 'E-mail'}
          </span>
        </div>
        <div className={styles.stepHead}>
          <Button type="button" variant="ghost" disabled={disabled || !canMoveUp} onClick={() => onMove(-1)}>
            ↑
          </Button>
          <Button type="button" variant="ghost" disabled={disabled || !canMoveDown} onClick={() => onMove(1)}>
            ↓
          </Button>
          <Button type="button" variant="ghost" disabled={disabled} onClick={onDuplicate}>
            Dupliceer
          </Button>
          <Button type="button" variant="ghost" disabled={disabled || steps.length <= 1} onClick={onDelete}>
            Verwijder
          </Button>
        </div>
      </div>

      <div className={styles.branchRow}>
        <Field label="Wacht (dagen)">
          <TextInput
            type="number"
            min={0}
            max={365}
            disabled={disabled}
            value={step.wait_days}
            onChange={(e) => onChange({ ...step, wait_days: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Kanaal">
          <select
            className={styles.sequenceListBtn}
            style={{ padding: '8px 12px' }}
            disabled={disabled}
            value={step.step_channel}
            onChange={(e) =>
              onChange({
                ...step,
                step_channel: e.target.value as 'email' | 'voice',
              })
            }
          >
            <option value="email">E-mail</option>
            <option value="voice">Voice (stub)</option>
          </select>
        </Field>
      </div>

      {isVoice ? (
        <p className={styles.voiceBanner}>
          Geen e-mail voor deze stap. De worker logt voice-touch en gaat door; echte dialer volgt in Wave 3.
        </p>
      ) : (
        <>
          <Field label="Bibliotheek-template (optioneel)">
            <select
              className={styles.sequenceListBtn}
              style={{ padding: '8px 12px', width: '100%' }}
              disabled={disabled}
              value={step.email_template_id ?? ''}
              onChange={(e) =>
                onChange({
                  ...step,
                  email_template_id: e.target.value || null,
                })
              }
            >
              <option value="">Handmatig onderwerp + body</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {!step.email_template_id ? (
            <>
              <Field label="Onderwerp">
                <TextInput
                  value={step.subject_template}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...step, subject_template: e.target.value })}
                />
              </Field>
              <Field label="Body (plain)">
                <TextArea
                  rows={4}
                  value={step.body_text_template}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...step, body_text_template: e.target.value })}
                />
              </Field>
            </>
          ) : tpl ? (
            <p className={styles.simPath}>
              Preview: {tpl.subject_template} · {tpl.name}
            </p>
          ) : null}
        </>
      )}

      <SequenceBranchRulesEditor
        stepOrder={step.step_order}
        rules={step.branch_rules}
        stepOrders={stepOrders}
        allStepsForValidation={steps.map((s) => ({
          step_order: s.step_order,
          branch_rules: s.branch_rules,
        }))}
        onChange={(branch_rules) => onChange({ ...step, branch_rules })}
        disabled={disabled}
      />
    </li>
  );
}
